import { processBinDataWithRange } from "../graphView/processBinData";
import { buildFishRowsForBinsFromDetail } from "./buildFishRowsForBins";

function binWidthFromOpts(opts) {
  return opts.binSizeValue ?? (opts.unitValue === "English" ? 1 : 10);
}

function formatOriginBinLabel(originStart, originEnd, unitValue) {
  if (unitValue === "English") {
    return `${originStart.toFixed(1)}-${originEnd.toFixed(1)}`;
  }
  return `${Math.round(originStart)}-${Math.round(originEnd - 1)}`;
}

/**
 * Prepends one empty bin below the first fish bin so cumulative lines start at 0%.
 * @param {{ binLabels: string[], numbers: number[], percents: number[], cumulative: number[] }} data
 */
export function prependOriginBin(data, opts, minLength) {
  if (!data?.binLabels?.length || !Number.isFinite(minLength)) return data;

  const binWidth = binWidthFromOpts(opts);
  const firstStart = Math.floor(minLength / binWidth) * binWidth;
  const originStart = firstStart - binWidth;
  if (originStart < 0) return data;

  const originEnd = firstStart;
  const originLabel = formatOriginBinLabel(originStart, originEnd, opts.unitValue);

  return {
    binLabels: [originLabel, ...data.binLabels],
    numbers: [0, ...data.numbers],
    percents: [0, ...data.percents],
    cumulative: [0, ...data.cumulative],
  };
}

/**
 * @returns {{ binLabels: string[], numbers: number[], percents: number[], cumulative: number[] }}
 */
export function binPercentsAndCumulative(fishRows, opts, minLength, maxLength) {
  const bins = processBinDataWithRange(fishRows, opts, minLength, maxLength);
  if (!bins.length) {
    return { binLabels: [], numbers: [], percents: [], cumulative: [] };
  }

  const numbers = bins.map((b) => Math.floor(b.number || 0));
  const stockingNums = bins.map((b) => Math.floor(b.stocking || 0));
  const samplingNums = numbers.map((n, i) => Math.max(0, n - stockingNums[i]));
  const total = samplingNums.reduce((a, b) => a + b, 0) || 1;
  const percents = samplingNums.map((n) => (n / total) * 100);
  let cum = 0;
  const cumulative = percents.map((p) => {
    cum += p;
    return Math.min(100, Number(cum.toFixed(6)));
  });

  const base = {
    binLabels: bins.map((b) => b.binLabel),
    numbers: samplingNums,
    percents,
    cumulative,
  };

  return prependOriginBin(base, opts, minLength);
}

export function globalLengthRangeForDetails(pondDetails, selectedFish, selectedUnit) {
  let minL = Infinity;
  let maxL = -Infinity;
  (pondDetails || []).forEach((detail) => {
    const rows = buildFishRowsForBinsFromDetail(detail, selectedFish, selectedUnit);
    rows.forEach((r) => {
      if (Number.isFinite(r.length)) {
        minL = Math.min(minL, r.length);
        maxL = Math.max(maxL, r.length);
      }
    });
  });
  if (!Number.isFinite(minL) || !Number.isFinite(maxL) || minL > maxL) {
    return { minLength: 0, maxLength: 1 };
  }
  return { minLength: minL, maxLength: maxL };
}
